import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthEndPoints, AuthService } from '../services/auth.service';
import { config } from '../../config/appConfig';
import { getDefaultError } from '../models/error';
import { SnackBarService } from '../services/snackbar.service';
import { UserEndPoints } from '../services/user.service';
import { SessionService } from '../services/session.service';
import { Store } from '@ngrx/store';
import {
  RefreshTokenAction,
  RefreshTokenSuccessAction,
} from '../store/user/user.actions';
import { Actions, ofType } from '@ngrx/effects';
import { LogoutAction } from '../store/auth/auth.actions';

// Exclusion List That Doesn't Require Any Token
const exclusionList: string[] = [];

const clientAccessKeyRequiredRoutes: string[] = [
  AuthEndPoints.clientInfo,
  AuthEndPoints.token,
  AuthEndPoints.otpGenerate,
  AuthEndPoints.otpVerify,
  AuthEndPoints.recaptchaVerify,
];

const tempTokenRequiredRoutes: string[] = [
  UserEndPoints.authorize,
  UserEndPoints.refreshToken,
];

const MAX_REVOKED_USER_RETRIES = 3;
@Injectable()
export class HttpTokenInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private snackBar = inject(SnackBarService);
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private store = inject(Store);
  private _componentSubscriptions: Subscription[] = [];
  private actions$ = inject(Actions);
  private destroy$ = new Subject<void>();
  private revokedUserRetryCount = 0;

  constructor() {
    this._componentSubscriptions.push(
      this.actions$
        .pipe(ofType(RefreshTokenSuccessAction), takeUntil(this.destroy$))
        .subscribe(async ({ response }) => {
          // On Refresh Token Success Action, Initalize the Session
          await this.sessionService.initiateSession();
        }),
    );
  }

  totalRequests = 0;
  requestsCompleted = 0;

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!req.url.includes('/live')) {
      this.totalRequests++;
    }

    let isPresent = false;
    // added to load string assets json files
    if (req.url.includes('/assets/i18n/')) {
      return next.handle(req);
    }
    let urlPath = new URL(req.url).pathname.toLowerCase(); // Using pathname for URL without query params
    const requiresClientAccessKey = clientAccessKeyRequiredRoutes.some(
      (route) => urlPath.includes(route.toLowerCase()),
    );
    const requiresTempToken = tempTokenRequiredRoutes.some((route) =>
      urlPath.includes(route.toLowerCase()),
    );

    for (let i = 0; i < exclusionList.length; i++) {
      isPresent = urlPath.includes(exclusionList[i].toLowerCase());
      if (isPresent) {
        break;
      }
    }

    const headersConfig: any = {
      Accept: 'application/json,*/*',
    };

    if (requiresClientAccessKey) {
      headersConfig['X-ACCESS-KEY'] = config.accessKey;
      headersConfig['X-Client-TYPE'] =
        req.headers.get('X-Client-TYPE') ?? config.clientType;
    }

    if (!(req.body instanceof FormData)) {
      headersConfig['Content-Type'] = 'application/json';
    }

    if (isPresent === false) {
      const token = this.authService.getTokenInformation()?.AccessToken;
      if (token) {
        headersConfig['Authorization'] = `Bearer ${token}`;
      }

      // Add the Session Token
      const sessionToken = this.sessionService.getSessionToken();
      if (sessionToken) {
        headersConfig['x-session-token'] = sessionToken;
      }
    }
    if (requiresTempToken) {
      headersConfig[
        'Authorization'
      ] = `Bearer ${this.authService.getSavedTempToken()}`;
    }

    if (req.headers.get('Content-Type')) {
      headersConfig['Content-Type'] = req.headers.get('Content-Type');
    }

    if (req.headers.get('Accept')) {
      headersConfig['Accept'] = req.headers.get('Accept');
    }

    req = req.clone({ setHeaders: headersConfig });

    return next
      .handle(req)
      .pipe(
        tap((event) => {
          if (event instanceof HttpResponse) {
            this.revokedUserRetryCount = 0;
          }
        }),
        finalize(() => {
          if (!req.url.includes('/live')) {
            this.requestsCompleted++;

            if (this.requestsCompleted === this.totalRequests) {
              this.totalRequests = 0;
              this.requestsCompleted = 0;
            }
          }
        }),
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          const errorCode = err?.error?.Errors?.[0]?.Code;
          const message = err?.error?.Errors?.[0]?.Message;
          const silentErrorCodes = [
            'devices-limit-exhausted',
            'browser-limit-exhausted',
            'screens-limit-exhausted',
            'free-session-revoked',
            'setting-error',
            'revoked-user',
          ];

          if (errorCode) {
            const error = getDefaultError(errorCode);
            // Don't show snackbar notification error messages for some error codes
            const displayMessage =
              errorCode === 'invalid-session-token'
                ? 'Session Expired.'
                : error?.useServerMessage
                ? message
                : `${error?.defaultErrorMessage} (Error Code: ${error?.errorCode})`;
            if (
              displayMessage &&
              !silentErrorCodes.includes(errorCode) &&
              errorCode !== 'invalid-session-token'
            ) {
              this.snackBar.showSnackBar('error', displayMessage, {
                duration: -1,
              });
            }

            // Grouped with deleted-user rather than with revoked-user:
            // both of those end the session, but revoked-user refreshes
            // the token, and a refresh cannot help here - the server
            // refuses the refresh for the same reason, so the retries
            // would be spent arriving back at this message.
            // organization-subscription-disabled: the user's organization
            // subscription (or the organization) is disabled - refused on
            // every request, and at sign-in and refresh, until enabled.
            if (
              errorCode === 'deleted-user' ||
              errorCode === 'role-disabled' ||
              errorCode === 'organization-subscription-disabled'
            ) {
              // Only when there is in fact a session to end. These also
              // come back from a *failed sign-in*, where logging out
              // would navigate away from the login page and take the
              // explanation off the screen with it.
              if (this.authService.getTokenInformation()?.RefreshToken) {
                this.store.dispatch(LogoutAction({ skipSessionLogout: true })); // force logout
              }
            }

            if (
              errorCode === 'free-session-revoked' ||
              errorCode === 'revoked-user'
            ) {
              if (errorCode === 'revoked-user') {
                // Guard: stop retrying after MAX_REVOKED_USER_RETRIES attempts
                if (this.revokedUserRetryCount >= MAX_REVOKED_USER_RETRIES) {
                  this.revokedUserRetryCount = 0; // reset for future logins
                  this.store.dispatch(
                    LogoutAction({ skipSessionLogout: true }),
                  ); // force logout
                  this.snackBar.showSnackBar(
                    'error',
                    'Your session has expired or been invalidated. Please sign in again to continue.',
                    { duration: -1 },
                  );
                  return EMPTY;
                }
                this.revokedUserRetryCount++;
              }
              // Refresh The Token
              this.store.dispatch(
                RefreshTokenAction({
                  request: {
                    Token:
                      this.authService.getTokenInformation()?.RefreshToken!,
                  },
                }),
              );
            }

            // when "invalid-session-token" error occurs on any API call (other than initiating session), it won't be handled by session service.
            // Initiate the session and the error will be handled by the session service
            if (errorCode === 'invalid-session-token') {
              this.sessionService.initiateSession().subscribe({
                next: (response) => {
                  // Do Something(might handle in the future)
                },
                error: (err) => {
                  // Do Something(might handle in the future)
                },
              });
            }

            // If There Is A Error In Case
            const redirectErrorCodes = [
              'validation-error',
              'invalid-operation',
              'resource-not-found',
            ];
            if (redirectErrorCodes.includes(errorCode)) {
              const path = location.pathname;
              // Regex for /books/{uuid}
              const isBooksPath = /^\/books\/[^\/]+$/.test(path);

              if (isBooksPath) {
                this.snackBar.showSnackBar(
                  'error',
                  'Access denied. Redirecting you back to Books.',
                  {
                    duration: 2000,
                  },
                );
                setTimeout(() => {
                  this.router.navigate(['/books']);
                }, 2000);
              }
            }
          }

          if (err?.status === 401) {
            setTimeout(() => {
              // Handle logout logic
            }, 50);
          }

          throw err;
        }),
      );
  }
}
